import { BufferAttribute, BufferGeometry, Matrix4, Vector3 } from 'three';
import type { HitPointInfo } from 'three-mesh-bvh';
import type HalfEdgeMesh from './HalfEdgeMesh.js';
import type { Face, Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';

const _centroid = new Vector3();
const _localOnTarget = new Vector3();
const _closestWorld = new Vector3();
const _invTarget = new Matrix4();

export interface FaceProximityHit {
  face: Face;
  distance: number;
}

export type FaceProximityFilter = (face: Face, centroidWorld: Vector3) => boolean;

/** World-space centroid of a triangular face. */
export function faceCentroidWorld(mesh: HalfEdgeMesh, face: Face, out: Vector3): void {
  const he = face.halfedge;
  out
    .copy(he.vertex.position)
    .add(he.next.vertex.position)
    .add(he.prev.vertex.position)
    .multiplyScalar(1 / 3);
  mesh.localToWorld(out);
}

/** Shortest distance from a world-space point to `targetMesh` surface (BVH). */
export function worldDistanceToMeshSurface(
  worldPoint: Vector3,
  targetMesh: HalfEdgeMesh,
): number | null {
  const boundsTree = targetMesh.geometry.boundsTree;
  if (!boundsTree?.closestPointToPoint) return null;

  targetMesh.updateWorldMatrix(true, false);
  _invTarget.copy(targetMesh.matrixWorld).invert();
  _localOnTarget.copy(worldPoint).applyMatrix4(_invTarget);

  const hit: HitPointInfo = {} as HitPointInfo;
  boundsTree.closestPointToPoint(_localOnTarget, hit);
  if (!hit.point) return null;

  _closestWorld.copy(hit.point).applyMatrix4(targetMesh.matrixWorld);
  return worldPoint.distanceTo(_closestWorld);
}

/**
 * Faces on `sourceMesh` whose centroid is within `maxDistWorld` of `targetMesh` surface.
 * Optional filter runs after the distance test (e.g. embrasure gate in segmentation).
 */
export function collectFacesNearMeshSurface(
  sourceMesh: HalfEdgeMesh,
  targetMesh: HalfEdgeMesh,
  maxDistWorld: number,
  filter?: FaceProximityFilter,
): FaceProximityHit[] {
  if (maxDistWorld <= 0) return [];

  sourceMesh.updateWorldMatrix(true, false);
  targetMesh.updateWorldMatrix(true, false);

  const boundsTree = targetMesh.geometry.boundsTree;
  if (!boundsTree?.closestPointToPoint) return [];

  _invTarget.copy(targetMesh.matrixWorld).invert();
  const hit: HitPointInfo = {} as HitPointInfo;
  const hits: FaceProximityHit[] = [];
  const snapshot = sourceMesh.halfEdges.faces.slice();

  for (const face of snapshot) {
    if (face.halfedge?.face !== face) continue;
    faceCentroidWorld(sourceMesh, face, _centroid);

    _localOnTarget.copy(_centroid).applyMatrix4(_invTarget);
    boundsTree.closestPointToPoint(_localOnTarget, hit);
    if (!hit.point) continue;
    _closestWorld.copy(hit.point).applyMatrix4(targetMesh.matrixWorld);
    const distance = _centroid.distanceTo(_closestWorld);
    if (distance >= maxDistWorld) continue;
    if (filter && !filter(face, _centroid)) continue;

    hits.push({ face, distance });
  }

  hits.sort((a, b) => a.distance - b.distance);
  return hits;
}

/** Builds a render-only geometry containing only the given faces (local space). */
export function buildGeometryFromFaceSet(mesh: HalfEdgeMesh, includedFaces: Set<Face>): BufferGeometry {
  const excluded = new Set<Face>();
  for (const face of mesh.halfEdges.faces) {
    if (!includedFaces.has(face)) excluded.add(face);
  }
  const geometry = mesh.halfEdges.excludedBufferGeometry(excluded).newGeometry;
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Builds a line geometry tracing only the OUTLINE (perimeter) of a face patch:
 * the edges whose neighbouring face across them is not part of the patch. Returns
 * `LineSegments`-ready positions (pairs of endpoints) in local space.
 *
 * @param liftLocal optional outward offset along the averaged patch normal, in
 *   local units — lifts the outline slightly off the surface so it doesn't
 *   z-fight with the tooth.
 */
export function buildFaceSetOutlineGeometry(
  _mesh: HalfEdgeMesh,
  includedFaces: Set<Face>,
  liftLocal = 0,
): BufferGeometry {
  const vertexNormals = liftLocal !== 0 ? patchVertexNormals(includedFaces) : null;

  const positions: number[] = [];
  const p = new Vector3();
  const pushVertex = (v: Vertex) => {
    p.copy(v.position);
    const n = vertexNormals?.get(v);
    if (n) p.addScaledVector(n, liftLocal);
    positions.push(p.x, p.y, p.z);
  };

  for (const face of includedFaces) {
    const he = face.halfedge;
    for (const edge of [he, he.next, he.prev]) {
      const across = edge.twin.face;
      if (!across || !includedFaces.has(across)) {
        pushVertex(edge.twin.vertex);
        pushVertex(edge.vertex);
      }
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3));
  return geometry;
}

/** Averaged (normalized) vertex normals over a face patch, for offsetting the outline outward. */
function patchVertexNormals(includedFaces: Set<Face>): Map<Vertex, Vector3> {
  const vertexNormals = new Map<Vertex, Vector3>();
  const faceNormal = new Vector3();
  for (const face of includedFaces) {
    face.getNormal(faceNormal);
    const he = face.halfedge;
    for (const v of [he.vertex, he.next.vertex, he.prev.vertex]) {
      const acc = vertexNormals.get(v);
      if (acc) acc.add(faceNormal);
      else vertexNormals.set(v, faceNormal.clone());
    }
  }
  for (const n of vertexNormals.values()) n.normalize();
  return vertexNormals;
}

/** Default proximity threshold from mean edge length (matches segmentation strip band). */
export function defaultProximityThresholdWorld(mesh: HalfEdgeMesh, factor = 0.55): number {
  const meanEdge = Math.max(mesh.halfEdges.getMeanEdgeLength?.() ?? 0.05, 1e-6);
  return meanEdge * factor;
}
